import React from 'react'
import {DASH, formatDecimal, formatEfficiency, formatHours, formatInt, percentOf} from '../../helpers/format'
import {deltaToneOf, efficiencyToneOf} from '../../helpers/efficiencyReport'
import PercentTag from '../shared/PercentTag'
import Sparkline from './Sparkline'

/**
 * Sáu thẻ của dải KPI — cùng khuôn với `efficiencyColumns.js`: một module trả DỮ LIỆU để trang vẽ,
 * không phải một component.
 *
 * ⚠️ Tách khỏi `OutputReportPage` vì hai lý do, và lý do thứ hai mới là lý do bắt buộc:
 *      1. 162 dòng toàn ternary nằm giữa một hàm 1.400 dòng thì không ai đọc nổi, và không test
 *         được — trong repo này không file test nào import một component nào.
 *      2. `eslint-plugin-react-hooks` bản repo đang dùng đếm đường đi trên TOÀN hàm và bộ đếm của
 *         nó tràn ở kích thước này. Khi tràn, nó báo NHẦM "React Hook is called conditionally" cho
 *         **mọi** hook trong file, kể cả hook cách chỗ vừa sửa 600 dòng. Đã mất hai lượt truy nhầm
 *         vì chuyện đó. Nó còn nhạy với HÌNH DẠNG đồ thị chứ không chỉ số nhánh: đổi một
 *         `.filter((s) => …)` thành một lời gọi helper — tức BỚT code — cũng đủ làm nó tràn.
 *
 *    Nói cách khác: giữ trang dưới ngưỡng đó là một ràng buộc thật của repo này, không phải sở thích.
 */
export const kpiCards = ({
    report,
    totals,
    efficiencyTotals,
    efficiencyThresholds,
    pureNote,
    noEfficiencyReason,
    breakdownLabel,
    redoBreakdown,
    caption,
    previousDelta,
    compareOpen,
}) => (report ? [
    {
        label: 'Tổng sản lượng công đoạn',
        tooltipKey: 'output',
        value: formatInt(totals.output),
        caption,
    },
    {
        label: 'Giờ định mức',
        tooltipKey: 'standardHours',
        value: `${formatHours(totals.standardHours, 1)} h`,
        caption: 'mức chuẩn 100%',
    },
    {
        label: 'Số lần làm lại',
        tooltipKey: 'redoCount',
        value: formatInt(totals.redoCount),
        caption: `${percentOf(totals.redoCount, totals.output)} tổng sản lượng`,
        /*
         * Một con số tổng không nói được phải đi sửa ở đâu. Tách theo công đoạn thì thấy ngay
         * làm lại dồn vào công đoạn nào — mà đó mới là câu hỏi người quản lý thật sự hỏi.
         *
         * Đặt trong Popover chứ không trải ra thẻ KPI: dải thẻ có 6 ô cạnh nhau, nhét thêm một
         * bảng vào một ô sẽ đẩy năm ô kia xuống và phá bố cục của cả hàng.
         */
        detail: redoBreakdown.length > 0 ? (
            <div className="VcRedoBreakdown">
                <div className="VcRedoBreakdown__title">Làm lại theo {breakdownLabel}</div>
                {/*
                  * Cột "Sản lượng" có mặt để lộ MẪU SỐ.
                  *
                  * ⚠️ Tỷ lệ ở đây và tỷ lệ trên thẻ KPI chia cho hai mẫu số KHÁC nhau: thẻ chia
                  *    cho TỔNG sản lượng của cả báo cáo, còn mỗi dòng chia cho sản lượng của
                  *    chính dòng đó. Cùng một tử số 32 vì thế ra 0,7% ở ngoài và 1,7% ở trong —
                  *    trông y như lỗi tính. Dòng ghi chú phía dưới đã nói điều đó từ đầu nhưng
                  *    không ai đọc chú thích trước khi nghi ngờ con số; in thẳng mẫu số ra thì
                  *    không còn gì để nghi.
                  */}
                <table>
                    <thead>
                        <tr>
                            <th>{breakdownLabel === 'nhân viên' ? 'Nhân viên' : 'Công đoạn'}</th>
                            <th className="VcRedoBreakdown__num">Làm lại</th>
                            <th className="VcRedoBreakdown__den">Sản lượng</th>
                            <th className="VcRedoBreakdown__pct">Tỷ lệ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {redoBreakdown.map((item) => (
                            <tr key={item.key}>
                                <td>{item.name}</td>
                                <td className="VcRedoBreakdown__num">{formatInt(item.redoCount)}</td>
                                <td className="VcRedoBreakdown__den">{formatInt(item.output)}</td>
                                <td className="VcRedoBreakdown__pct">{percentOf(item.redoCount, item.output)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="VcRedoBreakdown__note">
                    Phần trăm tính trên sản lượng của chính {breakdownLabel} đó — khác mẫu số với
                    tỷ lệ trên thẻ, vốn tính trên tổng sản lượng của cả báo cáo.
                </div>
            </div>
        ) : null,
    },
    {
        label: 'Đã có định mức',
        tooltipKey: 'hasStandard',
        value: <PercentTag ratio={totals.hasStandard} kind="standard"/>,
        caption: `thiếu ${formatInt(totals.noStandardOutput)} sản lượng`,
        tone: totals.hasStandard !== null && totals.hasStandard < 0.9 ? 'warn' : '',
    },
    {
        label: 'Đã có dữ liệu file in',
        tooltipKey: 'hasFileData',
        value: <PercentTag ratio={totals.hasFileData} kind="fileData"/>,
        caption: `thiếu ${formatInt(totals.noFileDataOutput)} sản lượng`,
        tone: totals.hasFileData !== null && totals.hasFileData < 0.75 ? 'bad' : '',
    },
    /*
     * Thẻ thứ SÁU — Hiệu suất (FAC-26417). Dải thẻ chuyển từ 5 sang 6 ô; `VcKpiStrip--six` dùng
     * lại đúng khuôn `--five` đang có, chỉ đổi số cột.
     *
     * ⚠️ Chưa ai nhập giờ thì hiện gạch ngang kèm CÂU GIẢI THÍCH, không hiện `0%`. `0%` đọc
     *    thành "cả xưởng làm việc không hiệu quả" trong khi sự thật là chưa có mẫu số — đúng
     *    loại bịa số mà RB-04 cấm, và là con số đầu tiên người dùng nhìn thấy trên màn hình.
     */
    {
        label: 'Hiệu suất',
        tooltipKey: 'efficiency',
        /*
         * ⚠️ KHÔNG dùng `PercentTag`: nó chỉ nhận `kind: 'standard' | 'fileData'` và tô theo
         *    ngưỡng ĐỘ PHỦ định mức — ngưỡng hoàn toàn khác ngưỡng hiệu suất. Truyền một `kind`
         *    lạ vừa làm propTypes cảnh báo vừa tô sai màu cho con số quan trọng nhất màn hình.
         */
        value:
            efficiencyTotals && efficiencyTotals.efficiency !== null ? (
                <span className="VcKpiEff">
                    <span className={`VcEffValue VcEffValue--${efficiencyToneOf(efficiencyTotals.efficiency, efficiencyThresholds) || 'D'}`}>
                        {formatEfficiency(efficiencyTotals.efficiency)}
                    </span>
                    {/*
                      * Chênh lệch so kỳ trước, tính bằng ĐIỂM phần trăm — không phải "tăng bao
                      * nhiêu %". Hiệu suất vốn đã là một tỷ số; "tăng 5%" của 90% là 94,5% hay
                      * 95%? Nói "5 điểm" thì không ai hỏi lại.
                      *
                      * Chỉ hiện khi khối so sánh đang MỞ: `toggleCompare` không gọi lại API lúc
                      * đóng, nên `previous` còn nguyên trong `report` và không gác thì con số
                      * kỳ trước nằm lại trên thẻ sau khi người dùng vừa bấm tắt so sánh.
                      */}
                    {/*
                      * ⚠️ Tô màu qua `deltaToneOf`, KHÔNG viết lại luật tại chỗ. Bản trước chép
                      *    tay `value > 0 ? xanh : đỏ` và bỏ mất nhánh `value === 0`, nên hai kỳ
                      *    bằng nhau hiện chip ĐỎ "▼0,0 điểm" — màn hình báo xấu cho một kỳ không
                      *    đổi. Và khi có thêm chỉ số đảo chiều lên thẻ KPI, bản chép tay sẽ tô
                      *    xanh cho tin xấu, đúng lỗi `invert` đã vá ở khối Tổng quan đội ngũ.
                      */}
                    {compareOpen && deltaToneOf(previousDelta) && (
                        <span className={`VcDeltaChip ${deltaToneOf(previousDelta)}`}>
                            {previousDelta > 0 ? '▲' : '▼'}
                            {formatDecimal(Math.abs(previousDelta) * 100, 1)} điểm
                        </span>
                    )}
                    {/* Đường theo ngày (FR-006) — cùng component với cột "Xu hướng" của bảng. */}
                    <Sparkline trend={efficiencyTotals.trend} width={110} height={26}/>
                </span>
            ) : (
                <span className="VcZero">{DASH}</span>
            ),
        /*
         * Hiệu suất THUẦN đi vào chú thích chứ không thành thẻ KPI thứ bảy: nó là cùng một
         * phép đo với con số ngay trên, chỉ bỏ phần làm lại — tách ra một thẻ riêng là mời
         * người đọc coi hai con số như hai chỉ tiêu độc lập. Chỉ hiện khi khác nhau sau khi
         * làm tròn, cùng luật với dòng phụ trong bảng (`pureEfficiencyNoteOf`).
         */
        caption:
            efficiencyTotals && efficiencyTotals.efficiency !== null
                ? [
                    pureNote ? `thuần ${formatEfficiency(pureNote.value)}` : null,
                    `${formatInt(efficiencyTotals.rankedActors)} tài khoản xếp hạng được`,
                    /*
                     * ⚠️ Giờ LÀM THỰC TẾ (`actualHours`), không phải giờ phân bổ. SRS FR-006 gọi
                     *    tên đúng con số này, và hai số lệch nhau rất xa ở nhóm In/Cắt/Khắc —
                     *    giờ có thật nhưng chưa quy đổi được nên phân bổ bằng 0. Thẻ in giờ
                     *    phân bổ là thẻ nói "cả xưởng làm 1.241 giờ" khi bảng chấm công ghi
                     *    2.669 giờ, và không chú thích nào giải thích khoảng cách đó.
                     */
                    efficiencyTotals.actualHours === null
                        ? null
                        : `${formatHours(efficiencyTotals.actualHours, 0)} h làm thực tế`,
                ].filter(Boolean).join(' · ')
                : noEfficiencyReason,
        /*
         * Ba mức, theo đúng thứ tự nghiêm trọng:
         *   · `bad`  — hiệu suất dưới ngưỡng hạng C, con số tự nó đã xấu;
         *   · `warn` — có ngày-công thiếu giờ, tức con số đang tính trên dữ liệu KHUYẾT
         *     (FR-006 đòi nền cảnh báo cho đúng ca này, vì người đọc không có cách nào khác
         *     để biết mẫu số còn thiếu bao nhiêu).
         */
        tone:
            efficiencyTotals
            && efficiencyTotals.efficiency !== null
            && efficiencyTotals.efficiency < (efficiencyThresholds ? efficiencyThresholds.c : 0.6)
                ? 'bad'
                : efficiencyTotals && efficiencyTotals.missingHourDays > 0
                    ? 'warn'
                    : '',
    },
] : [])
